import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/misc';
import { FormError } from '@/features/auth/auth-layout';
import { useUserOptions } from '@/hooks/use-user-options';
import { ApiRequestError } from '@/lib/api';
import { useCompanyOptions, useContactOptions, usePipelines, useSaveDeal } from './api';
import { BILLING_CYCLE_LABELS, DEAL_TYPE_LABELS, LEAD_SOURCE_LABELS } from './labels';
import type { BillingCycle, Deal, DealType, LeadSource } from './types';

const DEAL_TYPES = Object.keys(DEAL_TYPE_LABELS) as DealType[];
const BILLING_CYCLES = Object.keys(BILLING_CYCLE_LABELS) as BillingCycle[];
const SOURCES = Object.keys(LEAD_SOURCE_LABELS) as LeadSource[];

export function DealDialog({
  deal,
  pipelineId,
  companyId,
  onClose,
}: {
  deal?: Deal;
  pipelineId?: string;
  companyId?: string;
  onClose: () => void;
}) {
  const save = useSaveDeal();
  const { data: companies = [] } = useCompanyOptions();
  const { data: pipelines = [] } = usePipelines();
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState({
    title: deal?.title ?? '',
    companyId: deal?.companyId ?? companyId ?? '',
    contactId: deal?.contactId ?? '',
    pipelineId: deal?.pipelineId ?? pipelineId ?? '',
    dealType: deal?.dealType ?? ('CUSTOM_DEVELOPMENT' as DealType),
    value: deal?.value ?? '',
    recurringValue: deal?.recurringValue ?? '',
    billingCycle: deal?.billingCycle ?? '',
    probability: deal?.probability?.toString() ?? '',
    expectedCloseDate: deal?.expectedCloseDate?.slice(0, 10) ?? '',
    source: deal?.source ?? '',
    ownerId: deal?.ownerId ?? '',
  });

  // Contacts are scoped to the chosen company, so the picker cannot mismatch.
  const { data: contacts = [] } = useContactOptions(form.companyId || undefined);
  // /users/options, not /users: owning a deal does not make you an admin.
  const { data: users } = useUserOptions();

  const set = (patch: Partial<typeof form>) => setForm((current) => ({ ...current, ...patch }));

  const submit = async () => {
    setError(null);
    try {
      await save.mutateAsync({
        ...(deal ? { id: deal.id } : {}),
        title: form.title,
        ...(deal ? {} : { companyId: form.companyId, pipelineId: form.pipelineId || undefined }),
        contactId: form.contactId,
        dealType: form.dealType,
        value: form.value || undefined,
        recurringValue: form.recurringValue || undefined,
        billingCycle: form.billingCycle || undefined,
        probability: form.probability ? Number(form.probability) : undefined,
        expectedCloseDate: form.expectedCloseDate || undefined,
        source: form.source || undefined,
        ownerId: form.ownerId,
      });
      onClose();
    } catch (caught) {
      setError(caught instanceof ApiRequestError ? caught.message : 'Could not save the deal.');
    }
  };

  const isRecurring = form.dealType === 'SAAS_SUBSCRIPTION' || form.dealType === 'AMC';

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{deal ? `Edit ${deal.number}` : 'New deal'}</DialogTitle>
          <DialogDescription>
            {deal
              ? 'The company and pipeline are fixed once a deal exists — move it on the board instead.'
              : 'It starts in the first open stage of the pipeline you choose.'}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Title" className="sm:col-span-2">
            <Input
              value={form.title}
              autoFocus
              placeholder="What is this opportunity?"
              onChange={(event) => set({ title: event.target.value })}
            />
          </Field>

          {!deal && (
            <>
              <Field label="Company">
                <Select
                  value={form.companyId}
                  onChange={(event) => set({ companyId: event.target.value, contactId: '' })}
                >
                  <option value="">Choose a company</option>
                  {companies.map((company) => (
                    <option key={company.id} value={company.id}>
                      {company.name}
                    </option>
                  ))}
                </Select>
              </Field>

              <Field label="Pipeline">
                <Select
                  value={form.pipelineId}
                  onChange={(event) => set({ pipelineId: event.target.value })}
                >
                  <option value="">Default pipeline</option>
                  {pipelines.map((pipeline) => (
                    <option key={pipeline.id} value={pipeline.id}>
                      {pipeline.name}
                    </option>
                  ))}
                </Select>
              </Field>
            </>
          )}

          <Field label="Primary contact">
            <Select
              value={form.contactId}
              disabled={!form.companyId}
              onChange={(event) => set({ contactId: event.target.value })}
            >
              <option value="">Not set</option>
              {contacts.map((contact) => (
                <option key={contact.id} value={contact.id}>
                  {contact.name}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Deal type">
            <Select
              value={form.dealType}
              onChange={(event) => set({ dealType: event.target.value as DealType })}
            >
              {DEAL_TYPES.map((type) => (
                <option key={type} value={type}>
                  {DEAL_TYPE_LABELS[type]}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Value" hint="One-off contract value.">
            <Input
              inputMode="decimal"
              placeholder="0.00"
              value={form.value}
              onChange={(event) => set({ value: event.target.value })}
            />
          </Field>

          <Field label="Expected close">
            <Input
              type="date"
              value={form.expectedCloseDate}
              onChange={(event) => set({ expectedCloseDate: event.target.value })}
            />
          </Field>

          {isRecurring && (
            <>
              <Field label="Recurring value">
                <Input
                  inputMode="decimal"
                  placeholder="0.00"
                  value={form.recurringValue}
                  onChange={(event) => set({ recurringValue: event.target.value })}
                />
              </Field>
              <Field label="Billing cycle">
                <Select
                  value={form.billingCycle}
                  onChange={(event) => set({ billingCycle: event.target.value })}
                >
                  <option value="">Not set</option>
                  {BILLING_CYCLES.map((cycle) => (
                    <option key={cycle} value={cycle}>
                      {BILLING_CYCLE_LABELS[cycle]}
                    </option>
                  ))}
                </Select>
              </Field>
            </>
          )}

          <Field label="Probability %" hint="Leave blank to use the stage default.">
            <Input
              type="number"
              min={0}
              max={100}
              value={form.probability}
              onChange={(event) => set({ probability: event.target.value })}
            />
          </Field>

          <Field label="Source">
            <Select value={form.source} onChange={(event) => set({ source: event.target.value })}>
              <option value="">Not set</option>
              {SOURCES.map((source) => (
                <option key={source} value={source}>
                  {LEAD_SOURCE_LABELS[source]}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Owner">
            <Select value={form.ownerId} onChange={(event) => set({ ownerId: event.target.value })}>
              <option value="">Me</option>
              {(users ?? []).map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <FormError message={error} />

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={
              save.isPending || form.title.trim().length < 2 || (!deal && !form.companyId)
            }
          >
            {save.isPending ? 'Saving…' : deal ? 'Save changes' : 'Create deal'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label,
  hint,
  className,
  children,
}: {
  label: string;
  hint?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={`space-y-1.5 ${className ?? ''}`}>
      <Label>{label}</Label>
      {children}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}
